import type { SearchQuery } from "@/api/types";

export type SortKey = "relevance" | "newest" | "price_asc" | "price_desc" | "nearest" | "popular";

export interface SearchFilters {
  q: string;
  category: string;
  brand: string[];
  condition: string[];
  price_min?: number;
  price_max?: number;
  state?: number;
  lga?: number;
  verified: boolean;
  escrow: boolean;
  delivery: boolean;
  pay_on_delivery: boolean;
  posted_within?: number;
  attr: Record<string, string[]>;
  sort?: SortKey;
  lat?: number;
  lng?: number;
  radius_km?: number;
}

const BOOL_KEYS = ["verified", "escrow", "delivery", "pay_on_delivery"] as const;
const NUM_KEYS = ["price_min", "price_max", "state", "lga", "posted_within", "lat", "lng", "radius_km"] as const;
const ATTR = /^attr\[([a-z0-9_]+)\]$/;

const csv = (v: string | null) =>
  v
    ? v
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

export function parseFilters(params: URLSearchParams, category = ""): SearchFilters {
  const attr: Record<string, string[]> = {};
  params.forEach((value, key) => {
    const m = ATTR.exec(key);
    if (m?.[1]) attr[m[1]] = csv(value);
  });
  const f: SearchFilters = {
    q: params.get("q") ?? "",
    category: category || params.get("category") || "",
    brand: csv(params.get("brand")),
    condition: csv(params.get("condition")),
    verified: false,
    escrow: false,
    delivery: false,
    pay_on_delivery: false,
    attr,
  };
  BOOL_KEYS.forEach((k) => (f[k] = params.get(k) === "true"));
  NUM_KEYS.forEach((k) => {
    const raw = params.get(k);
    const n = raw === null || raw === "" ? NaN : Number(raw);
    if (!Number.isNaN(n)) f[k] = n;
  });
  const sort = params.get("sort");
  if (sort) f.sort = sort as SortKey;
  return f;
}

/** URL search params for the UI (category comes from the /c/:slug path when given). */
export function toSearchParams(f: SearchFilters, opts: { omitCategory?: boolean } = {}): URLSearchParams {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.category && !opts.omitCategory) p.set("category", f.category);
  if (f.brand.length) p.set("brand", f.brand.join(","));
  if (f.condition.length) p.set("condition", f.condition.join(","));
  NUM_KEYS.forEach((k) => f[k] !== undefined && p.set(k, String(f[k])));
  BOOL_KEYS.forEach((k) => f[k] && p.set(k, "true"));
  Object.entries(f.attr).forEach(([k, v]) => v.length && p.set(`attr[${k}]`, v.join(",")));
  if (f.sort) p.set("sort", f.sort);
  return p;
}

/** Query object for the typed API client. */
export function toApiQuery(f: SearchFilters, extra: Partial<SearchQuery> = {}): SearchQuery {
  const attr: Record<string, string> = {};
  Object.entries(f.attr).forEach(([k, v]) => v.length && (attr[k] = v.join(",")));
  const q: SearchQuery = {
    ...(f.q ? { q: f.q } : {}),
    ...(f.category ? { category: f.category } : {}),
    ...(f.brand.length ? { brand: f.brand.join(",") } : {}),
    ...(f.condition.length ? { condition: f.condition.join(",") } : {}),
    ...(Object.keys(attr).length ? { attr } : {}),
    ...(f.sort ? { sort: f.sort } : {}),
    ...extra,
  };
  NUM_KEYS.forEach((k) => {
    if (f[k] !== undefined) (q as Record<string, unknown>)[k] = f[k];
  });
  BOOL_KEYS.forEach((k) => {
    if (f[k]) (q as Record<string, unknown>)[k] = true;
  });
  return q;
}

export function activeFilterCount(f: SearchFilters): number {
  return (
    f.brand.length +
    f.condition.length +
    Object.values(f.attr).reduce((n, v) => n + v.length, 0) +
    BOOL_KEYS.filter((k) => f[k]).length +
    (f.price_min !== undefined || f.price_max !== undefined ? 1 : 0) +
    (f.state !== undefined ? 1 : 0) +
    (f.posted_within !== undefined ? 1 : 0)
  );
}

export function cursorFrom(next: string | null | undefined): string | undefined {
  if (!next) return undefined;
  try {
    return new URL(next).searchParams.get("cursor") ?? undefined;
  } catch {
    return undefined;
  }
}
